/**
 * Control Help Text Database
 * Provides contextual help for all form control types
 */

export interface ControlHelpInfo {
  title: string;
  description: string;
  useCases?: string[];
  tips?: string[];
}

export const controlHelpTexts: Record<string, ControlHelpInfo> = {
  // Input Controls
  text: {
    title: 'Text Input',
    description: 'Single-line text field for short text entries.',
    useCases: ['Names', 'Email addresses', 'Phone numbers', 'Usernames'],
    tips: ['Use for simple text', 'Add validation for email/phone', 'Set max length for consistency'],
  },
  textarea: {
    title: 'Text Area',
    description: 'Multi-line text field for longer text content.',
    useCases: ['Comments', 'Descriptions', 'Long-form text', 'Feedback'],
    tips: ['Supports multiple lines', 'Auto-expand on scroll', 'Set row height and max length'],
  },
  email: {
    title: 'Email Input',
    description: 'Specialized text field with email validation.',
    useCases: ['Email addresses', 'Contact forms'],
    tips: ['Automatic email format validation', 'Shows email keyboard on mobile'],
  },
  url: {
    title: 'URL Input',
    description: 'Text field with URL validation and formatting.',
    useCases: ['Website links', 'API endpoints', 'Document URLs'],
    tips: ['Validates URL format', 'Mobile-friendly', 'Auto-corrects protocol'],
  },
  password: {
    title: 'Password Input',
    description: 'Secure text input that masks characters.',
    useCases: ['Passwords', 'Security codes', 'PIN entry'],
    tips: ['Always transmit over HTTPS', 'Show/hide toggle available', 'Enforce strong password rules'],
  },
  number: {
    title: 'Number Input',
    description: 'Input field for numeric values with increment/decrement.',
    useCases: ['Quantities', 'Prices', 'Ratings', 'Age'],
    tips: ['Set min/max bounds', 'Configure decimal places', 'Use spinners for mobile'],
  },
  'rich-text-editor': {
    title: 'Rich Text Editor',
    description: 'Advanced editor with formatting options (bold, italic, links, etc).',
    useCases: ['Long-form content', 'Articles', 'Documentation', 'Formatted text'],
    tips: ['Supports WYSIWYG editing', 'Export to HTML', 'Custom toolbar configuration'],
  },

  // Selection Controls
  checkbox: {
    title: 'Checkbox',
    description: 'Multiple-choice input allowing zero or more selections.',
    useCases: ['Terms agreement', 'Feature selection', 'Multiple preferences'],
    tips: ['Group related checkboxes', 'Provide clear labels', 'Consider default state'],
  },
  radio: {
    title: 'Radio Button',
    description: 'Single-choice input allowing exactly one selection from a group.',
    useCases: ['Mutually exclusive options', 'Single selection', 'Preference choice'],
    tips: ['Always group radios', 'One must be selected', 'Provide "None" option if needed'],
  },
  switch: {
    title: 'Toggle Switch',
    description: 'On/off toggle for boolean values.',
    useCases: ['Feature toggles', 'Yes/No questions', 'Settings'],
    tips: ['Clear labels for on/off states', 'Immediate action', 'Good for mobile'],
  },
  select: {
    title: 'Dropdown Select',
    description: 'Single selection from a dropdown list.',
    useCases: ['Countries', 'Categories', 'Status selection'],
    tips: ['Use for 4+ options', 'Add search/filter for long lists', 'Provide placeholder'],
  },
  multiselect: {
    title: 'Multi-Select',
    description: 'Multiple selection from a list of options.',
    useCases: ['Tags', 'Skills', 'Multiple filters'],
    tips: ['Show selected items', 'Use search for large lists', 'Set max selections if needed'],
  },
  'tree-select': {
    title: 'Tree Select',
    description: 'Hierarchical selection with parent-child relationships.',
    useCases: ['Organizational hierarchy', 'Category trees', 'Nested selections'],
    tips: ['Useful for nested data', 'Parent auto-selects children', 'Collapsible branches'],
  },
  'tree-view': {
    title: 'Tree View',
    description: 'Displays hierarchical data as a collapsible tree list. Supports node-level action buttons to navigate to forms or pages.',
    useCases: [
      'File system or folder explorers',
      'Category hierarchies',
      'Org charts in list form',
      'Agent / workflow trees',
      'Menu or navigation structures',
    ],
    tips: [
      'Connect data via binding path or paste static JSON in properties',
      'Use {{fieldName}} templates in action URL buttons (e.g. /agent/{{id}})',
      'Set defaultExpanded to a depth number to pre-open the first N levels',
      'nodeLinkUrlField lets each node provide its own link URL from data',
    ],
  },
  'org-chart': {
    title: 'Org Chart',
    description: 'Renders a visual card-based org chart with connector lines. Each card shows name, title, description and optional avatar. Supports collapsible subtrees and per-node action buttons.',
    useCases: [
      'Team / company org charts',
      'Agent and AI system hierarchies',
      'Product / category tree visualisation',
      'Workflow node maps',
      'Reporting structure diagrams',
    ],
    tips: [
      'Data should be a single root node with nested children arrays',
      'Expected fields: id, name, title, description, children',
      'Use colorField to set per-node accent colors from your data',
      'Action buttons support URL templates: /detail/{{id}}',
      'Horizontal layout works better for wide, shallow trees',
    ],
  },
  'cascading-select': {
    title: 'Cascading Select',
    description: 'Dependent dropdowns where options update based on previous selection.',
    useCases: ['Country/State/City', 'Category/Subcategory', 'Dependent selections'],
    tips: ['First dropdown filters second', 'Maintain selection state', 'Handle async data loading'],
  },

  // Date/Time Controls
  date: {
    title: 'Date Picker',
    description: 'Calendar-based date selection.',
    useCases: ['Birth dates', 'Event dates', 'Deadlines'],
    tips: ['Show calendar popup', 'Keyboard navigation', 'Set min/max dates'],
  },
  datetime: {
    title: 'Date & Time',
    description: 'Combined date and time selection.',
    useCases: ['Event scheduling', 'Appointment booking', 'Timestamp entry'],
    tips: ['Separate date and time inputs', 'Timezone awareness', 'Show format example'],
  },
  'date-range-picker': {
    title: 'Date Range',
    description: 'Select start and end dates for a range.',
    useCases: ['Vacation dates', 'Project duration', 'Report periods'],
    tips: ['Validate start < end', 'Quick preset ranges', 'Visual range highlighting'],
  },
  'time-picker': {
    title: 'Time Picker',
    description: 'Select time with hours, minutes, and optionally seconds.',
    useCases: ['Appointment times', 'Business hours', 'Shift selection'],
    tips: ['12/24 hour format', 'Minute intervals', 'Keyboard shortcuts'],
  },

  // File Upload
  'file-upload': {
    title: 'File Upload',
    description: 'Allow users to upload files with drag-and-drop support.',
    useCases: ['Document upload', 'Image upload', 'Attachment submission'],
    tips: ['Show file type restrictions', 'Drag-drop support', 'Progress indicator'],
  },
  'document-scanner': {
    title: 'Document Scanner',
    description: 'Capture documents directly from device camera.',
    useCases: ['ID verification', 'Document capture', 'Receipt scanning'],
    tips: ['Mobile optimized', 'Auto-detect document edges', 'Multi-page support'],
  },

  // Visualization
  'bar-chart': {
    title: 'Bar Chart',
    description: 'Horizontal or vertical bar chart for categorical data.',
    useCases: ['Comparisons', 'Rankings', 'Distributions'],
    tips: ['Show axis labels', 'Add legend', 'Color-code categories'],
  },
  'line-chart': {
    title: 'Line Chart',
    description: 'Line graph for trends and continuous data.',
    useCases: ['Time series', 'Trends', 'Performance metrics'],
    tips: ['Show data points', 'Add grid lines', 'Tooltip on hover'],
  },
  'pie-chart': {
    title: 'Pie Chart',
    description: 'Pie chart for part-to-whole relationships.',
    useCases: ['Market share', 'Budget breakdown', 'Percentages'],
    tips: ['Show percentages', 'Limit to 5-6 slices', 'Use contrasting colors'],
  },
  'data-table': {
    title: 'Data Table',
    description: 'Display tabular data with sorting and pagination.',
    useCases: ['Lists', 'Reports', 'Data browsing'],
    tips: ['Add sorting', 'Pagination for large datasets', 'Alternating row colors'],
  },

  // Layout Controls
  'card-container': {
    title: 'Card Container',
    description: 'Container with card styling and shadow.',
    useCases: ['Grouped content', 'Feature cards', 'Information blocks'],
    tips: ['Use for visual grouping', 'Add hover effects', 'Responsive padding'],
  },
  'collapsible-panel': {
    title: 'Collapsible Panel',
    description: 'Expandable/collapsible content section.',
    useCases: ['FAQ sections', 'Optional content', 'Space-saving layouts'],
    tips: ['Start expanded or collapsed', 'Show expand/collapse icon', 'Smooth animation'],
  },
  accordion: {
    title: 'Accordion',
    description: 'Multiple collapsible sections with only one open at a time.',
    useCases: ['Multi-section forms', 'FAQs', 'Settings groups'],
    tips: ['Only one section open', 'Keyboard navigation', 'Smooth transitions'],
  },
  'enhanced-tabs': {
    title: 'Enhanced Tabs',
    description: 'Tab interface with advanced features.',
    useCases: ['Multi-section forms', 'Content organization', 'Step navigation'],
    tips: ['Show active tab clearly', 'Keyboard shortcuts', 'Lazy load content'],
  },
  'form-container': {
    title: 'Form Container',
    description: 'Multi-step form with various layout options (tabs, wizard, accordion).',
    useCases: ['Multi-step forms', 'Workflows', 'Onboarding flows'],
    tips: ['Choose layout carefully', 'Configure data binding', 'Validate between steps'],
  },
  modal: {
    title: 'Modal Dialog',
    description: 'Dialog box overlaying the main form.',
    useCases: ['Confirmations', 'Alerts', 'Secondary forms'],
    tips: ['Make dismissible', 'Set backdrop click behavior', 'Clear action buttons'],
  },

  // Text & Headers
  label: {
    title: 'Label',
    description: 'Read-only text label.',
    useCases: ['Display information', 'Section labels', 'Help text'],
    tips: ['Use semantic HTML', 'Include context', 'Style consistently'],
  },
  header: {
    title: 'Header',
    description: 'Heading for sections.',
    useCases: ['Section titles', 'Form titles', 'Instructions'],
    tips: ['Use proper heading levels', 'Keep concise', 'Maintain hierarchy'],
  },

  // Button
  button: {
    title: 'Button',
    description: 'Clickable button for form submission and custom actions.',
    useCases: ['Submit forms', 'Navigate to URLs', 'Trigger custom actions', 'Cancel operations'],
    tips: [
      'Set type to "submit" for form submission',
      'Set type to "button" for custom actions',
      'Use "primary", "secondary", or "danger" variants',
      'Configure action (navigate, custom) in button config',
      'Button must be added explicitly - no automatic buttons'
    ],
  },

  // Image & Media
  image: {
    title: 'Image',
    description: 'Display images with optional sizing and alt text.',
    useCases: ['Product images', 'Icons', 'Illustrations'],
    tips: ['Always set alt text', 'Optimize image size', 'Use responsive sizing'],
  },
  'image-gallery': {
    title: 'Image Gallery',
    description: 'Gallery with multiple images and lightbox view.',
    useCases: ['Product galleries', 'Photo albums', 'Image showcase'],
    tips: ['Thumbnail preview', 'Lightbox zoom', 'Keyboard navigation'],
  },

  // Code & Advanced
  'code-editor': {
    title: 'Code Editor',
    description: 'Syntax-highlighted code editor.',
    useCases: ['JSON/XML entry', 'Configuration', 'Script input'],
    tips: ['Syntax highlighting', 'Auto-indent', 'Line numbers'],
  },
  'json-editor': {
    title: 'JSON Editor',
    description: 'Specialized editor for JSON data with validation.',
    useCases: ['JSON configuration', 'Data structure entry', 'API request bodies'],
    tips: ['Real-time validation', 'Syntax highlighting', 'Format/beautify'],
  },
  'html-block': {
    title: 'HTML Block',
    description: 'Display custom HTML content.',
    useCases: ['Custom HTML', 'Embedded content', 'Rich formatting'],
    tips: ['Sanitize user input', 'Use for trusted content', 'Be cautious with scripts'],
  },

  // Pickers & Selectors
  'color-picker': {
    title: 'Color Picker',
    description: 'Color selection with visual picker.',
    useCases: ['Theme selection', 'Color preferences', 'Visual customization'],
    tips: ['Show color preview', 'Hex/RGB input', 'Preset colors'],
  },
  'location-picker': {
    title: 'Location Picker',
    description: 'Map-based location selection.',
    useCases: ['Address selection', 'Store locator', 'Delivery location'],
    tips: ['Show map', 'Address search', 'Geocoding support'],
  },
  search: {
    title: 'Search Input',
    description: 'Text input optimized for searching.',
    useCases: ['Search within data', 'Filter lists', 'Global search'],
    tips: ['Real-time search', 'Show suggestions', 'Clear button'],
  },
  'form-picker': {
    title: 'Form Picker',
    description: 'Search and select an existing Form by ID from the Forms API.',
    useCases: ['Select a form to reference', 'Form lookup in form containers', 'Form selection in workflows'],
    tips: ['Searches forms in real-time', 'Displays Form ID and Name', 'Integrates with Form Editor for editing'],
  },
  expression: {
    title: 'Expression Builder',
    description:
      'A field whose value is always authored through Expressions Studio: a read-only preview plus a button that opens the full wildcard editor in a modal.',
    useCases: [
      'Notification and email templates that must never be plain text',
      'Document-merge content built from a wildcard catalog',
      'Any field where a value is composed from system/person/math wildcards',
    ],
    tips: [
      'Edits are staged in the modal and written back only on OK',
      'Choose the delimiter syntax and output format in the properties panel',
      'The hosting app must call configureExpressionControl() to supply the wildcard catalog',
    ],
  },

  // Grid & Table
  grid: {
    title: 'Grid',
    description: 'Advanced grid for displaying and editing data.',
    useCases: ['Data editing', 'Large datasets', 'Complex tables'],
    tips: ['Add sorting/filtering', 'Inline editing', 'Pagination'],
  },
  table: {
    title: 'Table',
    description: 'Simple table for displaying data.',
    useCases: ['Data display', 'Comparison tables', 'Schedules'],
    tips: ['Clear headers', 'Responsive', 'Striped rows for readability'],
  },
};

/**
 * Get help info for a control type
 */
export const getControlHelpInfo = (controlType: string): ControlHelpInfo | null => {
  return controlHelpTexts[controlType] || null;
};

/**
 * Format help content as HTML for use in tooltips
 */
export const getHelpContentHTML = (info: ControlHelpInfo): { description: string; useCases?: string[]; tips?: string[] } => {
  return {
    description: info.description,
    useCases: info.useCases,
    tips: info.tips,
  };
};
